import * as React from "react";
import Link from "next/link";
import { pushSimpleEvent } from "@nju/data-layer";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { A, Panel, Separator, Text } from "@nju/ui";
import { Title } from "./partials";

export interface ISimEsimSettingsProps {
  isActiveButton: boolean;
  isUnsubscribedOrDisconnected: boolean;
  pukNumber: string | undefined;
  simNumber: string | undefined;
  cardStatus: string | undefined;
  userId: string | undefined;
  isEsim: boolean;
}

export function SimEsimSettings({
  isUnsubscribedOrDisconnected,
  isActiveButton,
  pukNumber,
  simNumber,
  cardStatus,
  userId,
  isEsim,
}: ISimEsimSettingsProps) {
  const simStatus = cardStatus === "N" ? "aktywna" : "zablokowana";
  const [isPukNumberVisible, setIsPukNumberVisible] = React.useState(false);

  const handlePushDataLayer = React.useCallback((eventName: string) => {
    pushSimpleEvent({ event: "activity_sim_status", eventName });
  }, []);

  return (
    <Panel testId="sim-card-settings-panel">
      <Title
        iconName="sim-card"
        title={`ustawienia karty ${isEsim ? "eSIM" : "SIM"}`}
      />
      <div className="flex flex-row items-center justify-between">
        <div className="flex items-center">
          <Icon
            height={24}
            name={
              simStatus === "aktywna"
                ? "check-circle-full"
                : "times-circle-full"
            }
            tone="accent"
            width={24}
          />
          <Text
            className="pl-3"
            size={{
              "@onlyMobile": "m",
              "@fromTablet": "l",
            }}
          >
            karta {simStatus}
          </Text>
        </div>
      </div>
      <Separator size="s" tone="invisible" />
      <div className="flex flex-col" data-cy="sim-card-number">
        <Text>numer karty</Text>
        <Text className="mt-1" weight="bold">
          {simNumber}
        </Text>
      </div>
      <Separator size="s" tone="invisible" />
      <div className="flex flex-col" data-cy="puk-number">
        <Text>numer PUK</Text>
        <div className="flex flex-row mt-1 items-center ">
          {isPukNumberVisible && (
            <Text className="w-20 tracking-[5px]" weight="bold">
              {pukNumber}
            </Text>
          )}
          {!isPukNumberVisible && (
            <div className="font-bold w-20 text-cerulean">
              &#9679; &#9679; &#9679; &#9679; &#9679; &#9679; &#9679; &#9679;
            </div>
          )}
          <div
            onClick={() => {
              setIsPukNumberVisible((isPukNumberVisible) => {
                return !isPukNumberVisible;
              });
            }}
            role="button"
          >
            <Icon
              className="ml-4"
              height={24}
              name={isPukNumberVisible ? "eye-slash" : "eye"}
              tone="neutral"
              width={27}
            />
          </div>
        </div>
      </div>
      <Separator size="s" tone="invisible" />
      <div className="flex flex-col from-tablet:items-center gap-2 text-center">
        {simStatus === "aktywna" && (
          <Link
            className="block w-full"
            href={portalRoutes.mojeKonto.mojaKarta.blokowanieKartySim.get({
              ...(userId && { userId }),
            })}
            passHref={true}
          >
            <A
              onClick={() => {
                handlePushDataLayer("block_sim_resignation_order");
              }}
              size={{
                "@onlyMobile": "s",
              }}
            >
              zablokuj kartę
            </A>
          </Link>
        )}
        {simStatus === "zablokowana" && (
          <Link
            className="block w-full"
            href={portalRoutes.mojeKonto.mojaKarta.odblokowanieKartySim.get({
              ...(userId && { userId }),
            })}
            passHref={true}
          >
            <A
              onClick={() => {
                handlePushDataLayer("unblock_sim_resignation_order");
              }}
              size={{
                "@onlyMobile": "s",
              }}
            >
              odblokuj kartę
            </A>
          </Link>
        )}
        {!isActiveButton && !isUnsubscribedOrDisconnected && (
          <Link
            className="block w-full"
            href={portalRoutes.mojeKonto.mojaKarta.wymianaKarty.index.get({
              ...(userId && { userId }),
            })}
            passHref={true}
          >
            <A
              onClick={() => {
                handlePushDataLayer("unblock_sim_resignation_order");
              }}
              size={{
                "@onlyMobile": "s",
              }}
              testId="change-card-button"
              variant="secondary"
            >
              wymień kartę
            </A>
          </Link>
        )}
      </div>
    </Panel>
  );
}
